/**
 * Client half of Web Push (NM-251): register the service worker, subscribe/
 * unsubscribe the browser, and hand the subscription to the server.
 *
 * Driven by the existing "Notify me when new stories arrive…" toggle: turning it
 * on (after the OS permission grant the toggle already requests) subscribes and
 * saves; turning it off unsubscribes and deletes. All of it no-ops when the
 * browser can't do push or push isn't configured (`state.pushPublicKey` null), so
 * the toggle still works as the foreground-only Notification-API feature it was.
 */

import { deletePushSubscription, savePushSubscription } from '../../app/api.js';

/** Whether this browser supports the Web Push stack at all. */
export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window
  );
}

/** VAPID keys are base64url; `PushManager.subscribe` wants the raw key bytes. */
function urlBase64ToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // An explicit ArrayBuffer (not `new Uint8Array(len)`, whose buffer is typed
  // `ArrayBufferLike`) so it satisfies `BufferSource` for `applicationServerKey`.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** Register (or reuse) the root-scope service worker. */
async function ensureRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  return existing ?? navigator.serviceWorker.register('/sw.js');
}

/**
 * Subscribe this browser to push and save the subscription (NM-251).
 *
 * Idempotent: reuses an existing `PushSubscription` if the browser already has
 * one for our key, so re-enabling the toggle just re-saves it. A no-op when push
 * is unsupported. Errors propagate so the caller can leave the setting off if the
 * subscription could not be made.
 */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  if (!pushSupported()) return;
  const registration = await ensureRegistration();
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }));
  const json = subscription.toJSON();
  if (json.endpoint === undefined) return;
  await savePushSubscription({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.['p256dh'], auth: json.keys?.['auth'] },
  });
}

/** Unsubscribe this browser and remove it from the account (NM-251). */
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  const subscription = registration === undefined ? null : await registration.pushManager.getSubscription();
  if (subscription === null) return;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  await deletePushSubscription(endpoint);
}
